// SIL weekly roster grid (Build 3b, spec 7 S3). Excel-like: one day tab, 30-min slot rows,
// a Ratio column + one column per staff. Paint ratios with the toolbar brush (click/drag),
// toggle staff cells (click/drag). Lock/unlock, Add Staff, Import ROC, Generate schedule.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Lock, Unlock, Save, UserPlus, Upload, CalendarPlus, Loader2, ArrowLeft, X, Users, Trash2 } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { isoDate } from '../../utils/time';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const RATIOS = [1, 2, 3, 4, 5, 6];
const slotLabel = (i) => `${String(Math.floor(i / 2)).padStart(2, '0')}:${i % 2 ? '30' : '00'}`;
const key = (dow, slot, staffId) => `${dow}-${slot}-${staffId || 'r'}`;

export default function SilTemplate() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tpl, setTpl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [day, setDay] = useState(0);
  const [fullDay, setFullDay] = useState(false);
  const [brush, setBrush] = useState({ ratioDen: 3, serviceType: 'Community Access', clear: false });
  // local editable state
  const [slotMap, setSlotMap] = useState({});   // `${dow}-${slot}` → {ratioDen, serviceType}
  const [staffSet, setStaffSet] = useState(new Set()); // `${dow}-${slot}-${staffId}`
  const [staffList, setStaffList] = useState([]);  // [{id,name}]
  const [dirtySlots, setDirtySlots] = useState(new Set());
  const [dirtyStaff, setDirtyStaff] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [addStaffOpen, setAddStaffOpen] = useState(false);
  const [rocOpen, setRocOpen] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const paint = useRef(null); // {col:'ratio'|staffId, state?}

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/sil/templates/${id}`);
      setTpl(data);
      const sm = {}; (data.slots || []).forEach(s => { sm[`${s.day_of_week}-${s.slot_index}`] = { ratioDen: s.ratio_den, serviceType: s.service_type }; });
      setSlotMap(sm);
      const ss = new Set(); const sl = new Map();
      (data.staff || []).forEach(s => { ss.add(`${s.day_of_week}-${s.slot_index}-${s.staff_id || 'open'}`); if (s.staff_id) sl.set(s.staff_id, s.staff_name); });
      setStaffSet(ss);
      // staff columns: any staff present + always an Open row
      const cols = [...sl.entries()].map(([id, name]) => ({ id, name }));
      setStaffList(cols);
      setDirtySlots(new Set()); setDirtyStaff(new Set());
    } catch { toast.error('Failed to load template'); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const locked = tpl?.is_locked;
  const slots = fullDay ? Array.from({ length: 48 }, (_, i) => i) : Array.from({ length: 33 }, (_, i) => i + 12); // 06:00–22:00

  // ── ratio paint ──
  const applyRatio = (slot) => {
    if (locked) return;
    const k = `${day}-${slot}`;
    setSlotMap(m => { const n = { ...m }; if (brush.clear) delete n[k]; else n[k] = { ratioDen: brush.ratioDen, serviceType: brush.serviceType }; return n; });
    setDirtySlots(d => new Set(d).add(k));
  };
  // ── staff paint ──
  const applyStaff = (slot, staffId, forceState) => {
    if (locked) return;
    const sid = staffId || 'open';
    const k = `${day}-${slot}-${sid}`;
    setStaffSet(s => {
      const n = new Set(s);
      const has = n.has(k);
      const state = forceState != null ? forceState : !has;
      if (state) n.add(k); else n.delete(k);
      return n;
    });
    setDirtyStaff(d => new Set(d).add(k));
  };

  const onCellDown = (col, slot) => {
    if (locked) return;
    if (col === 'ratio') { paint.current = { col: 'ratio' }; applyRatio(slot); }
    else { const sid = col || 'open'; const k = `${day}-${slot}-${sid}`; const state = !staffSet.has(k); paint.current = { col, state }; applyStaff(slot, col, state); }
  };
  const onCellEnter = (col, slot) => {
    if (!paint.current || locked) return;
    if (paint.current.col === 'ratio' && col === 'ratio') applyRatio(slot);
    else if (paint.current.col === col && col !== 'ratio') applyStaff(slot, col, paint.current.state);
  };
  useEffect(() => { const up = () => { paint.current = null; }; window.addEventListener('mouseup', up); return () => window.removeEventListener('mouseup', up); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const slotPayload = [...dirtySlots].map(k => { const [dw, sl] = k.split('-').map(Number); const v = slotMap[k]; return v ? { dayOfWeek: dw, slotIndex: sl, ratioDen: v.ratioDen, serviceType: v.serviceType, ratioNum: 1 } : { dayOfWeek: dw, slotIndex: sl, clear: true }; });
      const staffPayload = [...dirtyStaff].map(k => { const parts = k.split('-'); const dw = Number(parts[0]), sl = Number(parts[1]), sid = parts.slice(2).join('-'); const has = staffSet.has(k); return { dayOfWeek: dw, slotIndex: sl, staffId: sid === 'open' ? null : sid, value: has ? 'YES' : 'DELETE' }; });
      if (slotPayload.length) await api.put(`/sil/templates/${id}/slots`, { slots: slotPayload, unlock: !locked });
      if (staffPayload.length) await api.put(`/sil/templates/${id}/staff`, { cells: staffPayload, unlock: !locked });
      toast.success('Saved'); setDirtySlots(new Set()); setDirtyStaff(new Set());
    } catch (e) { toast.error(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };
  const toggleLock = async () => {
    try { await api.post(`/sil/templates/${id}/${locked ? 'unlock' : 'lock'}`, {}); setTpl(t => ({ ...t, is_locked: !locked })); }
    catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex justify-center h-64 items-center"><Loader2 className="w-8 h-8 animate-spin text-cyan-500" /></div>;
  if (!tpl) return <div className="p-6 text-slate-400">Template not found.</div>;

  const cols = [...staffList, { id: 'open', name: 'Open' }];

  return (
    <div className="p-5 max-w-7xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={() => navigate('/groups')} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50"><ArrowLeft size={15} /></button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-slate-900 truncate">{tpl.name}</h1>
          <p className="text-xs text-slate-400">{tpl.group_home_name || 'No home'} · {tpl.participants?.length || 0} participants · {tpl.template_type}</p>
        </div>
        <button onClick={toggleLock} className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold border ${locked ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
          {locked ? <Lock size={14} /> : <Unlock size={14} />} {locked ? 'Locked' : 'Unlocked'}
        </button>
        <button onClick={() => setAddStaffOpen(true)} className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50"><UserPlus size={14} /> Add Staff</button>
        <button onClick={() => setRocOpen(true)} className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50"><Upload size={14} /> Import ROC</button>
        <button onClick={() => setGenOpen(true)} className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-violet-200 bg-violet-50 text-violet-700 text-sm font-semibold hover:bg-violet-100"><CalendarPlus size={14} /> Generate</button>
        <button onClick={save} disabled={saving || (!dirtySlots.size && !dirtyStaff.size)} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-cyan-600 text-white text-sm font-semibold hover:bg-cyan-700 disabled:opacity-50">{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save{(dirtySlots.size + dirtyStaff.size) ? ` (${dirtySlots.size + dirtyStaff.size})` : ''}</button>
      </div>

      {/* Participants */}
      <div className="flex flex-wrap gap-1.5">
        {(tpl.participants || []).map(p => <span key={p.client_id} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600"><Users size={11} />{p.client_name}</span>)}
        {(tpl.participants || []).length === 0 && <span className="text-xs text-amber-600">No participants — add them on the Group Services page.</span>}
      </div>

      {/* Day tabs + brush */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex gap-0.5 bg-slate-100 rounded-xl p-0.5">
          {DAYS.map((d, i) => <button key={d} onClick={() => setDay(i)} className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${day === i ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'}`}>{d}</button>)}
        </div>
        <div className="w-px h-6 bg-slate-200" />
        <span className="text-xs text-slate-500">Ratio brush:</span>
        {RATIOS.map(n => <button key={n} onClick={() => setBrush(b => ({ ...b, ratioDen: n, clear: false }))} className={`px-2 py-1 text-xs rounded-lg border ${!brush.clear && brush.ratioDen === n ? 'border-cyan-400 bg-cyan-50 text-cyan-700 font-bold' : 'border-slate-200 text-slate-500'}`}>1:{n}</button>)}
        <button onClick={() => setBrush(b => ({ ...b, clear: true }))} className={`px-2 py-1 text-xs rounded-lg border ${brush.clear ? 'border-red-300 bg-red-50 text-red-600' : 'border-slate-200 text-slate-500'}`}>Clear</button>
        <input value={brush.serviceType} onChange={e => setBrush(b => ({ ...b, serviceType: e.target.value }))} placeholder="Service type" className="text-xs border border-slate-200 rounded-lg px-2 py-1 w-40" />
        <label className="ml-auto flex items-center gap-1.5 text-xs text-slate-500"><input type="checkbox" checked={fullDay} onChange={e => setFullDay(e.target.checked)} className="rounded accent-cyan-600" /> Full 24h</label>
      </div>

      {locked && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">Template is locked — click <b>Locked</b> to unlock before editing.</div>}

      {/* Grid */}
      <div className="border border-slate-200 rounded-xl overflow-auto max-h-[60vh] select-none" style={{ scrollbarWidth: 'thin' }}>
        <table className="w-full text-xs border-collapse">
          <thead className="sticky top-0 z-10">
            <tr className="bg-slate-50">
              <th className="px-2 py-2 text-left font-semibold text-slate-500 sticky left-0 bg-slate-50 w-16">Time</th>
              <th className="px-2 py-2 font-semibold text-slate-500 w-20 border-l border-slate-100">Ratio</th>
              {cols.map(c => <th key={c.id} className="px-2 py-2 font-semibold text-slate-600 border-l border-slate-100 min-w-[90px] truncate">{c.name}</th>)}
            </tr>
          </thead>
          <tbody>
            {slots.map(slot => {
              const r = slotMap[`${day}-${slot}`];
              return (
                <tr key={slot} className="border-t border-slate-50 hover:bg-slate-50/40">
                  <td className="px-2 py-1 text-slate-400 sticky left-0 bg-white">{slotLabel(slot)}</td>
                  <td onMouseDown={() => onCellDown('ratio', slot)} onMouseEnter={() => onCellEnter('ratio', slot)}
                    className={`px-1 py-1 text-center border-l border-slate-100 cursor-pointer ${r ? 'bg-cyan-50 text-cyan-700 font-semibold' : ''}`}
                    title={r?.serviceType || ''}>{r ? `1:${r.ratioDen}` : ''}</td>
                  {cols.map(c => {
                    const on = staffSet.has(`${day}-${slot}-${c.id}`);
                    return <td key={c.id} onMouseDown={() => onCellDown(c.id === 'open' ? 'open' : c.id, slot)} onMouseEnter={() => onCellEnter(c.id === 'open' ? 'open' : c.id, slot)}
                      className={`px-1 py-1 text-center border-l border-slate-100 cursor-pointer ${on ? (c.id === 'open' ? 'bg-amber-100' : 'bg-emerald-100') : ''}`}>{on ? '✓' : ''}</td>;
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400">Click or drag in the <b>Ratio</b> column to paint the selected ratio; click/drag a staff column to roster. Save persists changes; Generate turns the week into shifts.</p>

      {addStaffOpen && <AddStaffModal templateId={id} onClose={() => setAddStaffOpen(false)} onDone={() => { setAddStaffOpen(false); load(); }} />}
      {rocOpen && <RocImportModal templateId={id} onClose={() => setRocOpen(false)} onDone={() => { setRocOpen(false); load(); }} />}
      {genOpen && <GenerateModal templateId={id} onClose={() => setGenOpen(false)} />}
    </div>
  );
}

// ── Add Staff modal ──
function AddStaffModal({ templateId, onClose, onDone }) {
  const [staff, setStaff] = useState([]);
  const [staffId, setStaffId] = useState('');
  const [days, setDays] = useState([0, 1, 2, 3, 4]);
  const [start, setStart] = useState('09:00');
  const [end, setEnd] = useState('17:00');
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get('/staff').then(r => setStaff((Array.isArray(r.data) ? r.data : r.data.staff || []).filter(s => ['carer', 'support_worker'].includes(s.role)))).catch(() => {}); }, []);
  const toggleDay = (d) => setDays(ds => ds.includes(d) ? ds.filter(x => x !== d) : [...ds, d]);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/sil/templates/${templateId}/add-staff`, { staffId: staffId || null, days, startTime: start, endTime: end }); toast.success('Staff added to roster'); onDone(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setBusy(false); }
  };
  return (
    <Modal title="Add Staff to Roster" onClose={onClose}>
      <Field label="Worker"><select className="input-field" value={staffId} onChange={e => setStaffId(e.target.value)}><option value="">Open (unassigned)</option>{staff.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}</select></Field>
      <Field label="Days"><div className="flex flex-wrap gap-1.5">{DAYS.map((d, i) => <button key={d} onClick={() => toggleDay(i)} className={`px-2.5 py-1 text-xs rounded-lg border ${days.includes(i) ? 'border-cyan-400 bg-cyan-50 text-cyan-700' : 'border-slate-200 text-slate-500'}`}>{d}</button>)}</div></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Start"><input type="time" className="input-field" value={start} onChange={e => setStart(e.target.value)} /></Field>
        <Field label="End"><input type="time" className="input-field" value={end} onChange={e => setEnd(e.target.value)} /></Field>
      </div>
      <div className="flex justify-end gap-2 pt-2"><button onClick={onClose} className="btn-secondary btn-sm">Cancel</button><button onClick={submit} disabled={busy} className="btn-primary btn-sm">{busy ? 'Adding…' : 'Add'}</button></div>
    </Modal>
  );
}

// ── ROC import modal ──
function RocImportModal({ templateId, onClose, onDone }) {
  const [parsed, setParsed] = useState(null);
  const [busy, setBusy] = useState(false);
  const [serviceMap, setServiceMap] = useState({});
  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await api.post(`/sil/templates/${templateId}/import-roc`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setParsed(data);
      const sm = {}; (data.services || []).forEach(s => { sm[s.rocService] = { serviceType: s.suggestedServiceType, ratioDen: 1 }; });
      setServiceMap(sm);
      if (!data.detected) toast('Could not detect a roster layout — check the file', { icon: '⚠️' });
    } catch (e2) { toast.error(e2.response?.data?.message || 'Parse failed'); } finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/sil/templates/${templateId}/apply-roc`, { serviceMap, rows: parsed.rows }); toast.success(`Imported ${data.written} slot${data.written === 1 ? '' : 's'}${data.skipped ? `, ${data.skipped} skipped` : ''}`); onDone(); }
    catch { toast.error('Apply failed'); } finally { setBusy(false); }
  };
  return (
    <Modal title="Import Roster of Care (Excel)" onClose={onClose} wide>
      {!parsed ? (
        <div className="py-6 text-center">
          <input type="file" accept=".xlsx,.xls,.csv" onChange={onFile} className="text-sm" />
          <p className="text-xs text-slate-400 mt-2">Upload your NDIS ROC export. We'll detect participants, services and times, then let you map them before applying.</p>
          {busy && <Loader2 className="animate-spin mx-auto mt-3 text-cyan-500" />}
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <p className="text-xs font-semibold text-slate-600 mb-1">Participants found ({parsed.participants?.length || 0})</p>
            <div className="flex flex-wrap gap-1.5">{(parsed.participants || []).map((p, i) => <span key={i} className={`text-xs px-2 py-1 rounded-full ${p.suggestedClientId ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{p.rocName}{p.suggestedClientId ? ' ✓' : ' (no match)'}</span>)}</div>
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-600 mb-1">Service / ratio mapping</p>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {(parsed.services || []).map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 flex-1 truncate">{s.rocService}</span>
                  <input className="text-xs border border-slate-200 rounded-lg px-2 py-1 w-36" value={serviceMap[s.rocService]?.serviceType || ''} onChange={e => setServiceMap(m => ({ ...m, [s.rocService]: { ...m[s.rocService], serviceType: e.target.value } }))} />
                  <select className="text-xs border border-slate-200 rounded-lg px-1 py-1" value={serviceMap[s.rocService]?.ratioDen || 1} onChange={e => setServiceMap(m => ({ ...m, [s.rocService]: { ...m[s.rocService], ratioDen: parseInt(e.target.value, 10) } }))}>{RATIOS.map(n => <option key={n} value={n}>1:{n}</option>)}</select>
                </div>
              ))}
            </div>
          </div>
          <p className="text-xs text-slate-400">{parsed.rows?.length || 0} timetable rows parsed. Rows missing day/time are skipped on apply.</p>
          <div className="flex justify-end gap-2"><button onClick={() => setParsed(null)} className="btn-secondary btn-sm">Back</button><button onClick={apply} disabled={busy} className="btn-primary btn-sm">{busy ? 'Applying…' : 'Apply to grid'}</button></div>
        </div>
      )}
    </Modal>
  );
}

// ── Generate modal ──
function GenerateModal({ templateId, onClose }) {
  const fmt = isoDate;
  const [from, setFrom] = useState(fmt(new Date()));
  const [to, setTo] = useState(fmt(new Date(Date.now() + 13 * 86400000)));
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/sil/templates/${templateId}/generate`, { startDate: from, endDate: to }); setResult(data); toast.success(`Generated ${data.created} shift${data.created === 1 ? '' : 's'}`); }
    catch (e) { toast.error(e.response?.data?.message || 'Generate failed'); } finally { setBusy(false); }
  };
  return (
    <Modal title="Generate Service Schedule" onClose={onClose}>
      <p className="text-xs text-slate-400">Creates one group shift per rostered block per day, with per-participant ratio billing. Re-running skips days already generated.</p>
      <div className="grid grid-cols-2 gap-3">
        <Field label="From"><input type="date" className="input-field" value={from} onChange={e => setFrom(e.target.value)} /></Field>
        <Field label="To"><input type="date" className="input-field" value={to} onChange={e => setTo(e.target.value)} /></Field>
      </div>
      {result && <div className="text-xs bg-emerald-50 text-emerald-700 rounded-lg px-3 py-2">Created {result.created} shift{result.created === 1 ? '' : 's'}{result.skipped ? `, ${result.skipped} skipped (already existed)` : ''}.</div>}
      <div className="flex justify-end gap-2 pt-2"><button onClick={onClose} className="btn-secondary btn-sm">Close</button><button onClick={go} disabled={busy} className="btn-primary btn-sm">{busy ? 'Generating…' : 'Generate'}</button></div>
    </Modal>
  );
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${wide ? 'max-w-2xl' : 'max-w-md'} max-h-[90vh] flex flex-col`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100"><h3 className="font-semibold text-slate-800">{title}</h3><button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400"><X size={16} /></button></div>
        <div className="px-6 py-5 space-y-4 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}
function Field({ label, children }) { return <div><label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>{children}</div>; }
